import React, { createContext, useContext, useMemo } from "react";
import { FormProvider } from "react-hook-form";
import { useDockyardFormLogic } from "../hooks/useDockyardFormLogic";

type DockyardFormContextType = {
  currentCompany: any;
  currentProject: any;
  name: string | null;
  code: string | null;
  country: string;
  contributorOptions: any[];
  edbs: any[];
  hasEdbLinked: boolean;
  isIntervenantOpened: boolean;
  toggleIntervenantPopin: (isOpen: boolean) => void;
  handleSubmitIntervenant: (data: any) => Promise<any>;
  companyError: string;
  intervenantSubmitting: boolean;
  canEdit: boolean;
  dockyard?: any;
  defaultValuesForm?: any;
  error?: string;
  isSubmitting?: boolean;
};

export const DockyardFormContext = createContext<
  DockyardFormContextType | undefined
>(undefined);

export const useDockyardFormContext = () => {
  const context = useContext(DockyardFormContext);
  if (context === undefined) {
    throw new Error(
      "useDockyardFormContext must be used within DockyardFormProvider"
    );
  }
  return context;
};

type DockyardFormProviderProps = {
  children: React.ReactNode;
  dockyard?: any;
  error?: any;
  defaultValuesForm?: any;
  isSubmitting?: boolean;
};

export const DockyardFormProvider: React.FC<DockyardFormProviderProps> = ({
  children,
  dockyard,
  error,
  isSubmitting = false,
  defaultValuesForm = {},
}) => {
  const {
    form,
    currentCompany,
    currentProject,
    name,
    code,
    country,
    contributorOptions,
    edbs,
    hasEdbLinked,
    isIntervenantOpened,
    toggleIntervenantPopin,
    handleSubmitIntervenant,
    companyError,
    intervenantSubmitting,
    canEdit,
  } = useDockyardFormLogic(dockyard, defaultValuesForm);

  const value = useMemo(
    () => ({
      currentCompany,
      currentProject,
      name,
      code,
      country,
      contributorOptions,
      edbs,
      hasEdbLinked,
      isIntervenantOpened,
      toggleIntervenantPopin,
      handleSubmitIntervenant,
      companyError,
      intervenantSubmitting,
      canEdit,
      dockyard,
      defaultValuesForm,
      error,
      isSubmitting,
    }),
    [
      currentCompany,
      currentProject,
      name,
      code,
      country,
      contributorOptions,
      edbs,
      hasEdbLinked,
      isIntervenantOpened,
      toggleIntervenantPopin,
      handleSubmitIntervenant,
      companyError,
      intervenantSubmitting,
      canEdit,
      dockyard,
      defaultValuesForm,
      error,
      isSubmitting,
    ]
  );

  return (
    <DockyardFormContext.Provider value={value}>
      <FormProvider {...form}>{children}</FormProvider>
    </DockyardFormContext.Provider>
  );
};
